import { css } from 'aphrodite';
import * as React from 'react';
import { useFormContext } from 'react-hook-form';
import { Toggle } from '../../../../components/Toggle';
import { grayIconFill } from '../../../../styles/colors';
import { FormFieldWrapper } from '../../shared/components/FormFieldWrapper';
import { ShowSurveyNameToggle } from '../../shared/components/ShowSurveyNameToggle';
import { useSurveyHeaderCustomizationStatus } from '../../shared/hooks';
import { styleSheet } from '../styles';
import { EventRegistrationFormData } from '../utils';

// Component uses form context directly, no props needed
export const VisibilitySettings: React.FC = () => {
	const { control, setValue } = useFormContext<EventRegistrationFormData>();

	const { data: customHeaderData, hasSurveyHeaderCustomization } = useSurveyHeaderCustomizationStatus();

	// Without a header customization for surveys the event name is always rendered by the default
	// header, so the toggle is hidden and the value is forced on. The toggle only has an effect when a
	// custom header replaces that default header. We gate on `!error` so a failed status load — which
	// also reports `hasCustomization: false` — does not silently overwrite a value the user saved.
	React.useEffect(() => {
		if (customHeaderData && !customHeaderData.error && !hasSurveyHeaderCustomization) {
			setValue('showSurveyName', true);
		}
	}, [customHeaderData, hasSurveyHeaderCustomization, setValue]);

	return (
		<section className={css(styleSheet.section)}>
			<h3 className={css(styleSheet.sectionTitle)}>Visibility</h3>
			<p className={css(styleSheet.sectionDescription, styleSheet.sectionDescriptionNoWrap)}>
				Control where this event appears on your public events landing page. When enabled, this event will be visible to
				anyone who visits your events page.
			</p>

			<FormFieldWrapper
				name='showOnHomepage'
				control={control}
				title='Show on Events Landing Page'
				description='Display this event on your public events landing page'
			>
				{({ value, onChange }) => (
					<Toggle
						id='show-on-homepage-toggle'
						isOn={value}
						onToggleCheckChanged={() => onChange(!value)}
						uncheckedColor={grayIconFill}
					/>
				)}
			</FormFieldWrapper>
			{hasSurveyHeaderCustomization && (
				<ShowSurveyNameToggle<EventRegistrationFormData>
					name='showSurveyName'
					control={control}
					description='Display the event name at the top of the registration page'
					id='show-survey-name-toggle'
				/>
			)}
		</section>
	);
};
